import React, { PropsWithChildren } from 'react';
import { ModalProps } from 'pounceV2';

export interface InlineModalProps extends Omit<ModalProps, 'isOpen' | 'onClose'> {
  onClose?: () => void;
  onDismiss?: (event?: React.SyntheticEvent) => void;
}
interface ModalStateShape {
  modalContent: React.ReactElement | null;
  modalProps: InlineModalProps;
  open: boolean;
}

/* initial state of the reducer */
const initialState: ModalStateShape = {
  modalContent: null,
  modalProps: {},
  open: false,
};

export interface ModalContextValue {
  modalState: ModalStateShape;
  showModal: (content: React.ReactElement, modalProps?: InlineModalProps) => void;
  hideModal: () => void;
}

export const ModalContextV3 = React.createContext<ModalContextValue>(undefined);

/* A enhanced version of the context provider */
export const ModalProviderV3: React.FC<PropsWithChildren> = ({ children }) => {
  const [modalState, setModalState] = React.useState<ModalStateShape>(initialState);

  const showModal = React.useCallback((content, modalProps = {}) => {
    setModalState({
      modalContent: content,
      modalProps,
      open: true,
    });
  }, []);

  const hideModal = React.useCallback(() => {
    setModalState(prev => ({ ...prev, open: false }));
  }, []);

  const contextValue = React.useMemo(
    () => ({ modalState, hideModal, showModal }),
    [modalState, showModal, hideModal],
  );

  // make the `state` and `dispatch` available to the components
  return <ModalContextV3.Provider value={contextValue}>{children}</ModalContextV3.Provider>;
};
